import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  type OrderRecord, type QEPNotification, type QEPNotificationActionType, type QEPNotificationPayload, QEPNotificationStatus, QEPNotificationType, type SingleAlgoFullReport,
} from '@qep/types/src';
import {callAtTime, getQepSystem} from '@qep/shared/src';
import {getFileServer} from '@qep/shared/src/utils/runtime-config';
import {safeParseSchema} from '@qep/shared/src/schemas/custom-schema-validators';
import {
  notificationMessageTypes,
  notificationPostMessageSchema,
  type NotificationPostMessage,
} from '@qep/preloads/src/preloads/QEP/contracts/runtime-messages.js';
import {
  resolveHttpOrigin,
  resolveTrustedFrontendOrigins,
} from '@qep/preloads/src/preloads/QEP/contracts/message-boundary-security.js';
import Button from '@mui/material/Button';
import {Provider, useDispatch, useSelector} from 'react-redux';
import './NotificationCenter.scss';
import NotificationCard, {getStatusClass} from './notification-card.js';
import store, {type RootState, type AppDispatch} from './store.js';
import {
  setNotifications,
  removeNotification,
  clearNotifications,
  markAllAsRead,
  loadNotifications,
  addIncomingNotification,
  setFlashingNotification,
  selectFlashingNotification,
} from './notifications-slice.js';
import {getOrderTitleBase, processAlgoNotification, processOrderNotification} from './notification-api.js';

const delegatedLocalToastById = new Map<string, {close: () => void}>();
const delegatedLocalToastCleanupTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Accepts notification traffic only from this Browser window or an exact owned iframe on a configured QEP origin. */
const isTrustedNotificationEvent = (event: MessageEvent): boolean => {
  if (event.source === globalThis && event.origin === globalThis.location.origin) {
    return true;
  }

  const trustedFrontendOrigins = resolveTrustedFrontendOrigins(getFileServer(), globalThis.location.origin);
  for (const iframe of document.querySelectorAll('iframe')) {
    const iframeOrigin = resolveHttpOrigin(iframe.src, document.baseURI);
    if (
      iframe.contentWindow === event.source
      && iframeOrigin === event.origin
      && trustedFrontendOrigins.has(event.origin)
    ) {
      return true;
    }
  }

  return false;
};

const delegatedLocalToastMapTtlMs = 10_000;

/**
 * Clears the auto-cleanup timer for a delegated toast id so manual dismiss does not leak timers.
 */
function cleanupDelegatedToastTimer(id: string) {
  const timer = delegatedLocalToastCleanupTimers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
  }

  delegatedLocalToastCleanupTimers.delete(id);
}

/**
 * Handles postMessage from embedded component iframes that delegate WINDOW Noty toasts to the shell
 * so they render above all component surfaces (e.g. algo monitoring). Also handles programmatic dismiss.
 */
function handleDelegatedLocalToastMessage(message: NotificationPostMessage) {
  if (message.type === notificationMessageTypes.localToast) {
    const {method, title, id} = message.data;
    cleanupDelegatedToastTimer(id);

    const qep = getQepSystem();
    const notyHandle = qep.notification.local[method](title) as {close?: () => void} | undefined;
    if (notyHandle && typeof notyHandle.close === 'function') {
      delegatedLocalToastById.set(id, notyHandle as {close: () => void});
      const timer = setTimeout(() => {
        delegatedLocalToastById.delete(id);
        delegatedLocalToastCleanupTimers.delete(id);
      }, delegatedLocalToastMapTtlMs);
      delegatedLocalToastCleanupTimers.set(id, timer);
    }

    return;
  }

  if (message.type === notificationMessageTypes.localDismiss) {
    const {id} = message.data;
    cleanupDelegatedToastTimer(id);
    const entry = delegatedLocalToastById.get(id);
    delegatedLocalToastById.delete(id);
    entry?.close();
  }
}

type NotificationCenterProps = {
  logout: () => void;
  reportIssue: () => void;
};

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  logout = () => {/* no-op */},
  reportIssue = () => {/* no-op */},
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useSelector((state: RootState) => state.notifications.notifications);
  const runningAlgosRef = useRef<Record<string, string>>({});
  const loaded = useSelector((state: RootState) => state.notifications.loaded);
  const user = useSelector((state: RootState) => state.notifications.user);
  const flashingNotification = useSelector(selectFlashingNotification);
  const [anchorElement, setAnchorElement] = useState<undefined | HTMLElement>(undefined);
  const [viewedNotifications, setViewedNotifications] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<QEPNotificationType | undefined>(undefined);
  const [textFilter, setTextFilter] = useState('');
  const notificationTypes = [
    QEPNotificationType.ORDER,
    QEPNotificationType.ALGO,
    QEPNotificationType.ALERT,
    QEPNotificationType.SYSTEM,
  ];
  /** Removes expired notifications from the Browser's in-memory notification state. */
  const removeOldNotifications = () => {
    const sevenDaysAgo = new Date(new Date().setDate(new Date().getDate() - 7));
    const updatedNotifications: Array<QEPNotification<QEPNotificationActionType>> = notifications.filter(notification => new Date(notification.timestamp).getTime() > sevenDaysAgo.getTime());
    dispatch(setNotifications(updatedNotifications));
  };

  // Load auth info and notifications on mount
  useEffect(() => {
    void dispatch(loadNotifications());
  }, [dispatch]);

  // Wait for authentication to load before subscribing to order updates.
  useEffect(() => {
    if (!loaded) {
      return;
    }

    if (!getQepSystem()?.apiServices?.orderSummary?.subscribeToOrderSummary) {
      return () => {
        // no-op
      };
    }

    const unsubscribe = getQepSystem().apiServices.orderSummary.subscribeToOrderSummary((error, orders) => {
      if (error) {
        console.error('Error subscribing to order summary', error);
        return;
      }

      if (orders?.topic === 'UPDATE' && !Array.isArray(orders.payload)) {
        const {payload} = orders;
        const orderPayload = payload as OrderRecord;
        if (orderPayload.orderId && Number(orderPayload.msgType) === 9) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          dispatch(addIncomingNotification({
            id: orderPayload.orderId,
            type: QEPNotificationType.ORDER,
            title: `${getOrderTitleBase(orderPayload)}ACTION REJECTED`,
            shortDescription: `${orderPayload.orderId} action rejected`,
            payload: orderPayload,
            status: QEPNotificationStatus.CRITICAL,
            timestamp: new Date().toISOString(),
          }));
          return;
        }

        let notificationInfo: QEPNotificationPayload<any> | undefined;
        if (typeof payload.algoRunId === 'string' && typeof payload.algoStatus === 'string') {
          if (runningAlgosRef?.current[payload.algoRunId]
            && runningAlgosRef?.current[payload.algoRunId].trim() === payload.algoStatus.trim()) {
            return;
          }

          runningAlgosRef.current[payload.algoRunId] = payload.algoStatus.trim();

          notificationInfo = processAlgoNotification(payload as SingleAlgoFullReport, user);
        } else {
          notificationInfo = processOrderNotification(orderPayload, user);
        }

        if (!notificationInfo) {
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        dispatch(addIncomingNotification(notificationInfo));
      }
    });
    return () => {
      unsubscribe();
    };
  }, [dispatch, loaded, user]);

  useEffect(() => {
    const stopTimer = callAtTime(() => {
      removeOldNotifications();
    }, new Date(new Date().setHours(17, 35, 0, 0)));

    return () => {
      stopTimer();
    };
  }, [notifications]);

  /** Opens the menu and marks every visible notification as read in local state. */
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
    dispatch(setFlashingNotification(undefined)); // Stop flashing when the menu is opened
    setViewedNotifications(notifications.map(n => String(n.id)));
    const notisToMarkRead = notifications.filter(n => !n.isRead);
    if (notisToMarkRead.length > 0) {
      dispatch(markAllAsRead());
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleNotification);

    return () => {
      window.removeEventListener('message', handleNotification);
    };
  }, [notifications]);

  function handleNotification(event: MessageEvent) {
    if (!isTrustedNotificationEvent(event)) {
      return;
    }

    const parseResult = safeParseSchema(notificationPostMessageSchema, event.data);
    if (!parseResult.success) {
      return;
    }

    const message = parseResult.output as NotificationPostMessage;
    if (message.type === notificationMessageTypes.localToast || message.type === notificationMessageTypes.localDismiss) {
      handleDelegatedLocalToastMessage(message);
      return;
    }

    if (message.type === notificationMessageTypes.notification) {
      // Preserve nullable legacy status values accepted by the shared wire schema at the local ingestion boundary.
      const notification: QEPNotificationPayload<QEPNotificationActionType> = {
        ...message.data,
        status: message.data.status as QEPNotificationStatus | undefined,
      };
      void dispatch(addIncomingNotification(notification));
    }
  }

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const getSortedNotifications = useCallback(() => {
    // Sort notifications by status priority, then by timestamp (descending)
    const priority = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      CRITICAL: 1, SYSTEM: 2, HIGH: 3, MEDIUM: 4, LOW: 5,
    };
    return [...notifications].sort((a, b) => {
      const statusDiff = priority[a.status] - priority[b.status];
      if (statusDiff !== 0) {
        return statusDiff;
      }

      // Newest first
      return (Number(b.timestamp) || 0) - (Number(a.timestamp) || 0);
    });
  }, [notifications]);

  const sortedNotifications = getSortedNotifications();

  const filteredNotifications = sortedNotifications.filter(n => {
    const matchesType = typeFilter ? n.type === typeFilter : true;
    const matchesText = textFilter.trim() === ''
      || (n.title?.toLowerCase().includes(textFilter.toLowerCase()))
      || (n.shortDescription?.toLowerCase().includes(textFilter.toLowerCase()));
    return matchesType && matchesText;
  });

  /** Removes one notification from the Browser's local notification state. */
  const handleRemoveNotification = (id: string) => {
    dispatch(removeNotification(id));
  };

  /** Clears all notifications and their flashing indicator from local state. */
  const handleRemoveAll = () => {
    dispatch(clearNotifications());
    dispatch(setFlashingNotification(undefined));
  };

  return (
    <div className='browser-notification-container'>
      <div className='browser-notification-icon-button-container'>
        {flashingNotification && (
          <div className={`${getStatusClass(flashingNotification.status)} browser-notification-flashing`}>{flashingNotification.title}</div>
        )}
        <IconButton color='inherit' onClick={handleOpen}>
          <Badge badgeContent={notifications.filter(n => !n.isRead).length} color='error'>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </div>
      <Menu
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={handleClose}
        classes={{paper: 'browser-notification-menu'}}
      >
        {/* Notification Type Filters & Text Filter */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 16px', borderBottom: '1px solid #eee', alignItems: 'center',
        }}>
          <Button
            variant={typeFilter === undefined ? 'contained' : 'outlined'}
            size='small'
            onClick={() => {
              setTypeFilter(undefined);
            }}
          >All</Button>
          {notificationTypes.map(type => (
            <Button
              key={type}
              variant={typeFilter === type ? 'contained' : 'outlined'}
              size='small'
              onClick={() => {
                setTypeFilter(type);
              }}
            >{type}</Button>
          ))}
          <input
            type='text'
            placeholder='Filter notifications...'
            value={textFilter}
            onChange={event => {
              setTextFilter(event.target.value);
            }}
            style={{
              flex: 1, minWidth: 120, maxWidth: 220, marginLeft: 8, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 14,
            }}
          />
        </div>
        <div style={{maxHeight: 400, overflowY: 'auto'}}>
          {filteredNotifications.length === 0
            ? (
              <MenuItem disabled>No active notifications</MenuItem>
            )
            : (
              filteredNotifications.map(notification => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  viewedNotifications={viewedNotifications}
                  flashingNotification={flashingNotification}
                  onRemove={handleRemoveNotification}
                  onReportIssue={reportIssue}
                  onLogout={logout}
                  setFlashingNotification={setFlashingNotification}
                  setViewedNotifications={setViewedNotifications}
                />
              ))
            )}
        </div>
        {filteredNotifications.length > 0 && <MenuItem key='clear-all' disabled={notifications.length === 0} onClick={() => {
          setViewedNotifications([]);
          dispatch(setFlashingNotification(undefined));
          handleRemoveAll();
          setAnchorElement(undefined);
        }}>
          <div>Clear All</div>
        </MenuItem>}
      </Menu>
    </div>
  );
};

const NotificationCenterWithProvider = (props: NotificationCenterProps) => (
  <Provider store={store}>
    <NotificationCenter {...props} />
  </Provider>
);

export default NotificationCenterWithProvider;
